import type { FormElement } from 'form-definition';
import { getConditionalChanges } from '@beamery/lib-convert';
import type { MetaOverrides } from '@beamery/lib-convert/cjs/types';
import type { Submission, SubmissionAnswer } from './form.interface';

type Answer = {
  field: string;
  answer?: string | string[] | boolean | null;
};

const processAnswer = (submissionAnswer: SubmissionAnswer) => {
  if (submissionAnswer instanceof File) {
    return submissionAnswer.name;
  }
  return submissionAnswer;
};

const toAnswer = (
  element: FormElement,
  submissionAnswer: SubmissionAnswer,
): Answer | null =>
  element.field
    ? {
        field: element.field,
        answer: processAnswer(submissionAnswer),
      }
    : null;

const overrideMeta = <T extends FormElement>(
  element: T,
  overrides: MetaOverrides | undefined,
): T => {
  if (!overrides || !element.meta) {
    return element;
  }

  if (
    typeof element.meta === 'object' &&
    'options' in element.meta &&
    overrides.options
  ) {
    return {
      ...element,
      meta: {
        ...element.meta,
        options: overrides.options,
      },
    };
  }
  return element;
};

export const getElementsToShow = (
  formElements: FormElement[],
  localFormValues: Submission,
): FormElement[] => {
  const elementsToShow: FormElement[] = [];
  const processedAnswers: Answer[] = [];

  const crmFormValues = formElements
    .filter((elem) => 'value' in elem && elem.value !== null)
    .reduce((map, elem) => {
      if ('value' in elem) {
        return {
          ...map,
          [elem.id]: elem.value,
        } as Submission;
      }
      return map;
    }, {} as Submission);

  formElements.forEach((element) => {
    const conditionalChanges = getConditionalChanges(element, processedAnswers);

    if (conditionalChanges.visible) {
      const updatedElement = overrideMeta(
        element,
        conditionalChanges.metaOverrides,
      );
      elementsToShow.push(updatedElement);

      if (element.id in localFormValues) {
        const answer = toAnswer(element, localFormValues[element.id]);
        if (answer) {
          processedAnswers.push(answer);
        }
      } else if (element.id in crmFormValues) {
        const answer = toAnswer(element, crmFormValues[element.id]);
        if (answer) {
          processedAnswers.push(answer);
        }
      }
    }
  });

  return elementsToShow;
};
